import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AssignmentInd, Refresh, VerifiedUser, WarningAmber } from '@mui/icons-material';
import contractorVerificationService from '../services/contractorVerificationService';
import verifierService from '../services/verifierService';

const statusOptions = ['all', 'submitted', 'under_review', 'needs_more_info', 'completed', 'approved', 'approved_with_limits', 'rejected', 'suspended', 'revoked', 'expired', 'draft'];

// Chari's 4-state outcome field (contractor_verifications.verification_status).
const verificationStatusOptions = ['all', 'pending', 'in_review', 'approved', 'rejected'];
const VERIFICATION_STATUS_LABEL = { pending: 'Pending', in_review: 'In review', approved: 'Approved', rejected: 'Rejected' };
const VERIFICATION_STATUS_COLOR = { pending: 'default', in_review: 'info', approved: 'success', rejected: 'error' };

const AdminContractorVerificationsPage = () => {
  const [status, setStatus] = useState('all');
  const [verificationStatus, setVerificationStatus] = useState('in_review');
  const [applications, setApplications] = useState([]);
  const [selected, setSelected] = useState(null);
  const [review, setReview] = useState({ action: 'approve', badgeType: 'eqaya_verified', score: 75, riskLevel: 'standard', publicSummary: '', internalNotes: '' });
  const [assignment, setAssignment] = useState({
    verifierId: '',
    dueAt: '',
    requiredSiteVisitCount: 0,
    gpsProofMode: 'required',
    gpsOverrideReason: '',
    assignmentNotes: ''
  });
  const [evidencePack, setEvidencePack] = useState(null);
  const [verifiers, setVerifiers] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await contractorVerificationService.listAdmin({ status, verificationStatus });
      setApplications(response.data?.applications || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load contractor verifications');
    } finally {
      setLoading(false);
    }
  }, [status, verificationStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const openReview = async (application) => {
    setError('');
    try {
      const [response, packResponse, verifierResponse] = await Promise.all([
        contractorVerificationService.getAdmin(application.id),
        contractorVerificationService.getEvidencePack(application.id),
        verifierService.getAllVerifiers({}, { limit: 100 })
      ]);
      setSelected(response.data);
      setEvidencePack(packResponse.data || null);
      const assignmentRow = packResponse.data?.assignment;
      setAssignment({
        verifierId: assignmentRow?.verifier_id || '',
        dueAt: assignmentRow?.due_at ? String(assignmentRow.due_at).slice(0, 16) : '',
        requiredSiteVisitCount: assignmentRow?.required_site_visit_count ?? 0,
        gpsProofMode: assignmentRow?.gps_proof_mode || 'required',
        gpsOverrideReason: assignmentRow?.gps_override_reason || '',
        assignmentNotes: assignmentRow?.assignment_notes || ''
      });
      setVerifiers(Array.isArray(verifierResponse?.data) ? verifierResponse.data : []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load application');
    }
  };

  const assignVerifier = async () => {
    if (!selected) return;
    if (!assignment.verifierId) {
      setError('Choose a verifier before assigning this contractor file.');
      return;
    }
    if (assignment.gpsProofMode === 'admin_override' && !assignment.gpsOverrideReason.trim()) {
      setError('GPS override reason is required when GPS proof is disabled.');
      return;
    }

    setError('');
    try {
      const response = await contractorVerificationService.assignVerifier(selected.id, {
        verifierId: assignment.verifierId,
        dueAt: assignment.dueAt || null,
        requiredSiteVisitCount: Math.max(0, Number(assignment.requiredSiteVisitCount) || 0),
        gpsProofMode: assignment.gpsProofMode,
        gpsOverrideReason: assignment.gpsOverrideReason,
        assignmentNotes: assignment.assignmentNotes
      });
      const packResponse = await contractorVerificationService.getEvidencePack(selected.id);
      setEvidencePack(packResponse.data || null);
      setSuccess(response.message || 'Verifier assigned');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign verifier');
    }
  };

  const submitReview = async () => {
    setError('');
    try {
      await contractorVerificationService.review(selected.id, {
        ...review,
        score: Number(review.score),
        scopeLimits: review.action === 'approve_with_limits'
          ? { note: review.publicSummary || 'Approved with Eqaya-defined project and category limits.' }
          : {}
      });
      setSelected(null);
      setSuccess('Contractor verification updated');
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to review contractor');
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>Contractor Verifications</Typography>
          <Typography color="text.secondary">
            Review company, tax, portfolio, and reference evidence before issuing EQAYA Verified badges.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Outcome</InputLabel>
            <Select label="Outcome" value={verificationStatus} onChange={(event) => setVerificationStatus(event.target.value)}>
              {verificationStatusOptions.map((option) => (
                <MenuItem key={option} value={option}>{option === 'all' ? 'all' : VERIFICATION_STATUS_LABEL[option]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Workflow status</InputLabel>
            <Select label="Workflow status" value={status} onChange={(event) => setStatus(event.target.value)}>
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>{option.replaceAll('_', ' ')}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button startIcon={<Refresh />} onClick={load} disabled={loading}>Refresh</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Contractor</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Outcome</TableCell>
              <TableCell>Workflow</TableCell>
              <TableCell>Badge</TableCell>
              <TableCell>Risk</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {applications.map((application) => (
              <TableRow key={application.id} hover>
                <TableCell>
                  <Typography variant="subtitle2">
                    {[application.first_name, application.last_name].filter(Boolean).join(' ') || application.contractor_email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{application.contractor_email}</Typography>
                </TableCell>
                <TableCell>{application.company_name || application.trading_name || application.legal_name || 'Not provided'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={VERIFICATION_STATUS_COLOR[application.verification_status] || 'default'}
                    label={VERIFICATION_STATUS_LABEL[application.verification_status] || (application.verification_status || 'pending')}
                  />
                </TableCell>
                <TableCell><Chip size="small" variant="outlined" label={(application.status || '').replaceAll('_', ' ')} /></TableCell>
                <TableCell>{application.badge_type.replaceAll('_', ' ')}</TableCell>
                <TableCell>{application.risk_level}</TableCell>
                <TableCell>{application.updated_at ? new Date(application.updated_at).toLocaleDateString() : '-'}</TableCell>
                <TableCell align="right">
                  <Button size="small" variant="outlined" startIcon={<VerifiedUser />} onClick={() => openReview(application)}>
                    Review
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!applications.length && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No contractor verification applications found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="md">
        <DialogTitle>Review Contractor Verification</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h6">{selected.trading_name || selected.company_name || selected.legal_name || 'Contractor'}</Typography>
                <Typography color="text.secondary">{selected.contractor_email}</Typography>
              </Box>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {(selected.requirements || []).map((item) => (
                  <Chip key={item.key} color={item.complete ? 'success' : 'default'} label={`${item.label}: ${item.complete ? 'submitted' : 'missing'}`} />
                ))}
              </Stack>
              <Typography variant="subtitle2">Documents: {selected.documents?.length || 0}</Typography>
              <Typography variant="subtitle2">Portfolio evidence: {selected.portfolioEvidence?.length || 0}</Typography>
              <Typography variant="subtitle2">References: {selected.references?.length || 0}</Typography>
              <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                  <AssignmentInd color="primary" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>Field verifier assignment</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Same verifier login; this contractor file appears in their verifier work queue.
                    </Typography>
                  </Box>
                  {evidencePack?.assignment && (
                    <Chip size="small" label={evidencePack.assignment.status.replaceAll('_', ' ')} />
                  )}
                </Stack>

                {evidencePack?.assignment?.gps_proof_mode === 'admin_override' && (
                  <Alert severity="warning" icon={<WarningAmber />} sx={{ mb: 2 }}>
                    GPS proof was disabled by admin override. Final approval should consider the compensating evidence and override reason.
                  </Alert>
                )}

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="Verifier"
                    value={assignment.verifierId}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, verifierId: e.target.value }))}
                  >
                    {verifiers.map((verifier) => {
                      const verifierId = verifier.user_id || verifier.userId || verifier.id;
                      const name = [verifier.first_name, verifier.last_name].filter(Boolean).join(' ') || verifier.email || verifierId;
                      return (
                        <MenuItem key={verifierId} value={verifierId}>
                          {name}
                        </MenuItem>
                      );
                    })}
                  </TextField>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Due date"
                    InputLabelProps={{ shrink: true }}
                    value={assignment.dueAt}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, dueAt: e.target.value }))}
                  />
                  <TextField
                    fullWidth
                    type="number"
                    label="Required site visits"
                    value={assignment.requiredSiteVisitCount}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, requiredSiteVisitCount: e.target.value }))}
                    inputProps={{ min: 0, max: 10 }}
                    helperText="0 = not required (default). Set a count to mandate geofenced visits for this file."
                  />
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <TextField
                    select
                    fullWidth
                    label="GPS proof"
                    value={assignment.gpsProofMode}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, gpsProofMode: e.target.value }))}
                  >
                    <MenuItem value="required">Required</MenuItem>
                    <MenuItem value="admin_override">Admin override</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    label="GPS override reason"
                    value={assignment.gpsOverrideReason}
                    onChange={(e) => setAssignment((prev) => ({ ...prev, gpsOverrideReason: e.target.value }))}
                    disabled={assignment.gpsProofMode !== 'admin_override'}
                    required={assignment.gpsProofMode === 'admin_override'}
                  />
                </Stack>

                <TextField
                  fullWidth
                  multiline
                  minRows={2}
                  label="Assignment notes"
                  value={assignment.assignmentNotes}
                  onChange={(e) => setAssignment((prev) => ({ ...prev, assignmentNotes: e.target.value }))}
                  sx={{ mb: 2 }}
                />
                <Button variant="outlined" startIcon={<AssignmentInd />} onClick={assignVerifier}>
                  {evidencePack?.assignment ? 'Update Assignment' : 'Assign Verifier'}
                </Button>

                {evidencePack?.assignment && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Submitted evidence pack</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Chip label={`Representative: ${evidencePack.representativeCheck?.status || 'pending'}`} size="small" />
                      <Chip label={`Site visits: ${evidencePack.siteVisits?.length || 0}`} size="small" />
                      <Chip label={`GPS: ${evidencePack.assignment.gps_proof_mode.replaceAll('_', ' ')}`} size="small" color={evidencePack.assignment.gps_proof_mode === 'admin_override' ? 'warning' : 'default'} />
                      {evidencePack.assignment.recommendation && (
                        <Chip label={`Recommendation: ${evidencePack.assignment.recommendation.replaceAll('_', ' ')}`} size="small" color="info" />
                      )}
                    </Stack>
                    {evidencePack.assignment.verifier_summary && (
                      <Typography variant="body2" sx={{ mt: 1 }}>{evidencePack.assignment.verifier_summary}</Typography>
                    )}
                  </Box>
                )}
              </Box>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField select fullWidth label="Action" value={review.action} onChange={(e) => setReview((prev) => ({ ...prev, action: e.target.value }))}>
                  <MenuItem value="approve">Approve</MenuItem>
                  <MenuItem value="approve_with_limits">Approve with limits</MenuItem>
                  <MenuItem value="request_info">Request more information</MenuItem>
                  <MenuItem value="reject">Reject</MenuItem>
                  <MenuItem value="suspend">Suspend</MenuItem>
                  <MenuItem value="revoke">Revoke</MenuItem>
                </TextField>
                <TextField select fullWidth label="Badge" value={review.badgeType} onChange={(e) => setReview((prev) => ({ ...prev, badgeType: e.target.value }))}>
                  <MenuItem value="profile_checked">Profile checked</MenuItem>
                  <MenuItem value="eqaya_verified">EQAYA Verified</MenuItem>
                  <MenuItem value="verified_plus">Verified Plus</MenuItem>
                  <MenuItem value="specialist_verified">Specialist Verified</MenuItem>
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField fullWidth type="number" label="Score" value={review.score} onChange={(e) => setReview((prev) => ({ ...prev, score: e.target.value }))} />
                <TextField select fullWidth label="Risk level" value={review.riskLevel} onChange={(e) => setReview((prev) => ({ ...prev, riskLevel: e.target.value }))}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="elevated">Elevated</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                </TextField>
              </Stack>
              <TextField multiline minRows={2} label="Public badge summary / limits" value={review.publicSummary} onChange={(e) => setReview((prev) => ({ ...prev, publicSummary: e.target.value }))} />
              <TextField multiline minRows={3} label="Internal review notes" value={review.internalNotes} onChange={(e) => setReview((prev) => ({ ...prev, internalNotes: e.target.value }))} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelected(null)}>Cancel</Button>
          <Button variant="contained" onClick={submitReview}>Submit Review</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminContractorVerificationsPage;
